import { passkeyClient } from "@better-auth/passkey/client"
import { organizationClient } from "better-auth/client/plugins"
import { createAuthClient } from "better-auth/react"

// Same origin as the SPA: the Worker serves both the assets and /api/auth/*.
export const authClient = createAuthClient({
  basePath: "/api/auth",
  plugins: [passkeyClient(), organizationClient({ teams: { enabled: true } })],
})

export type Session = typeof authClient.$Infer.Session

/** A failed Better Auth call, with its error code ("INVALID_PASSWORD", "ORGANIZATION_SLUG_ALREADY_TAKEN", …). */
export class AuthCallError extends Error {
  constructor(
    message: string,
    readonly code: string | null,
    readonly status: number | null,
  ) {
    super(message)
    this.name = "AuthCallError"
  }
}

type AuthResult<T> = {
  readonly data: T | null
  readonly error: {
    readonly message?: string | undefined
    readonly code?: string | undefined
    readonly status?: number | undefined
  } | null
}

/** The data of a Better Auth client call, or an `AuthCallError` with the server's code. */
export const unwrap = <T>(result: AuthResult<T>): T => {
  if (result.error)
    throw new AuthCallError(
      result.error.message ?? "Request failed",
      result.error.code ?? null,
      result.error.status ?? null,
    )
  return result.data as T
}

/** The Better Auth error code of an error, if it has one. */
export const authCode = (error: unknown): string | null =>
  error instanceof AuthCallError ? error.code : null
